import React, { useMemo } from 'react';
import proptypes from 'prop-types';
const { string, oneOf, bool } = proptypes;

import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { Skeleton, SkeletonBlock } from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import { useSpecialBuy } from '../../context/SpecialBuyProvider';
import { CategoryTab } from '../CategoryTab/CategoryTab';
import { MobileCategoryTab } from '../MobileCategoryTab/MobileCategoryTab';

const generateKey = (categoryId, index) => `category-tab-${categoryId || index}`;

const SpecialBuyCategories = ({ entryId, specialBuyType, enableRoundedProductImages }) => {
  const {
    categories,
    selectedCategory,
    setSelectedCategory,
    isCondensedLayout,
    isSpecialBuyDataLoading
  } = useSpecialBuy();

  const categoriesMemoized = useMemo(() => {
    if (isCondensedLayout) {
      const impressionData = {
        id: entryId,
        name: specialBuyType === 'sbotd' ? 'SBOTD' : 'SBOTW',
        component: 'SpecialBuyBanner',
        type: 'product',
        position: '',
        category: ''
      };

      const mobileCategoryTab = categories.map((category, index) => (
        <MobileCategoryTab
          category={category}
          entryId={entryId}
          index={index + 1}
          key={generateKey(category.categoryId, index)}
          enableRoundedImages={enableRoundedProductImages}
        />
      ));

      return (
        <ImpressionProvider data={impressionData}>
          {mobileCategoryTab}
        </ImpressionProvider>
      );
    }

    return categories.map((category, index) => (
      <CategoryTab
        category={category}
        isSelected={category === selectedCategory}
        key={generateKey(category.categoryId, index)}
        onClick={() => setSelectedCategory(category)}
      />
    ));

  }, [
    categories,
    entryId,
    isCondensedLayout,
    selectedCategory,
    setSelectedCategory,
    specialBuyType,
    enableRoundedProductImages
  ]);

  const CategoryPlaceholder = () => {
    return (
      <div data-testid="product-pod-placeholder" className="sui-min-h-475px">
        <Skeleton
          grow
          data-component="CategoryPlaceholder"
          width="700px"
        >
          <SkeletonBlock aspect="square" width="100%" height="100%" />
        </Skeleton>
      </div>
    );
  };

  const categoryStyle = classNames('sui-flex sui-gap-2', {
    'sui-justify-between': isCondensedLayout,
    'sui-mb-4': !isCondensedLayout
  });

  return (
    <section className={categoryStyle}>
      {isSpecialBuyDataLoading ? <CategoryPlaceholder /> : [categoriesMemoized]}
    </section>
  );
};

SpecialBuyCategories.displayName = 'SpecialBuyCategories';

SpecialBuyCategories.propTypes = {
  entryId: string,
  specialBuyType: oneOf(['sbotd', 'pro']).isRequired,
  enableRoundedProductImages: bool,
};

SpecialBuyCategories.defaultProps = {
  entryId: '',
  enableRoundedProductImages: false,
};

export { SpecialBuyCategories };
